import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setAuthView,
  setSignupData,
  setForgotTempPassword,
  closeAuth,
  setSigninPassword,
} from '../../redux/slice/authModalSlice';
import { Images } from '../../utils/imageConstants';
import {
  ConfirmForgotPasswordCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  ResendConfirmationCodeCommand,
  InitiateAuthCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import toast from "react-hot-toast";
import {
  clientRegion,
  generateSecretHash,
  generateForgotFlowTempPassword,
  setAuthToken,
} from '../../helper/helper';
import { useNavigate } from 'react-router-dom';

const OtpModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const signupEmail = useAppSelector((s) => s.authModal.signupEmail);
  const emailForReset = useAppSelector((s) => s.authModal.emailForReset);
  const email = signupEmail || emailForReset;
  const userId = useAppSelector((s) => s.authModal.signupUserId);
  const signinPassword = useAppSelector((s) => s.authModal.signinPassword);
  const signupPassword = useAppSelector((s) => s.authModal.signupPassword);
  const [otpValues, setOtpValues] = useState<string[]>(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // Determine flow: signup (has userId), signin confirmation (has signupEmail but no userId and has password), or forgot password (has emailForReset)
  const isSignupFlow = !!userId;
  const isSigninConfirmationFlow = !!signupEmail && !userId && !emailForReset && !!signinPassword;
  const isForgotPasswordFlow = !!emailForReset;

  const otpComplete = otpValues.join('').length === 6;

  const handleBack = () => {
    if (isForgotPasswordFlow) {
      dispatch(setAuthView('forgot'));
      return;
    }
    if (isSignupFlow) {
      dispatch(setAuthView('signup'));
      return;
    }
    if (isSigninConfirmationFlow) {
      dispatch(setSigninPassword(undefined));
      dispatch(setAuthView('signin'));
      return;
    }
    dispatch(setAuthView('signin'));
  };

  // Timer countdown effect
  useEffect(() => {
    let interval: number;
    
    if (isTimerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsTimerActive(false);
    }

    return () => clearInterval(interval);
  }, [isTimerActive, timeLeft]);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtpValues = [...otpValues];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newOtpValues[i] = pastedData[i];
    }
    
    setOtpValues(newOtpValues);
    
    const nextEmptyIndex = newOtpValues.findIndex(val => val === '');
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5;
    inputRefs.current[focusIndex]?.focus();
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (!email) {
      toast.error("Email not found");
      return;
    }

    try {
      const secretHash = generateSecretHash(email);
      
      if (isSignupFlow || isSigninConfirmationFlow) {
        // Signup flow or signin confirmation flow - resend confirmation code
        const command = new ResendConfirmationCodeCommand({
          ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
          Username: email,
          SecretHash: secretHash,
        });

        await clientRegion.send(command);
      } else if (isForgotPasswordFlow) {
        // Forgot password flow - resend forgot password code
        const command = new ForgotPasswordCommand({
          ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
          Username: email,
          SecretHash: secretHash,
        });

        await clientRegion.send(command);
      }

      toast.success("OTP resent to your email");
      setTimeLeft(60);
      setIsTimerActive(true);
      setOtpValues(['', '', '', '', '', '']);
    } catch (err) {
      console.error("Resend OTP error:", err);
      toast.error("Failed to resend OTP");
    }
  };

  // Handle verify OTP (must not run unless 6 digits — also blocks Enter/implicit submit)
  const handleVerify = async () => {
    const otpString = otpValues.join('');
    if (otpString.length !== 6 || isVerifying) {
      return;
    }

    if (!email) {
      toast.error("Session expired. Please try again.");
      dispatch(setAuthView('signin'));
      return;
    }

    setIsVerifying(true);

    try {
      const secretHash = generateSecretHash(email);
      
      // Check flow type
      if (isSignupFlow) {
        // Signup flow - Confirm Sign Up
        const confirmCommand = new ConfirmSignUpCommand({
          ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
          Username: email,
          ConfirmationCode: otpString,
          SecretHash: secretHash,
        });

        await clientRegion.send(confirmCommand);

        // authenticate user 
        const authSecretHash = generateSecretHash(email);
        const authCommand = new InitiateAuthCommand({
          AuthFlow: "USER_PASSWORD_AUTH",
          ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
          AuthParameters: {
            USERNAME: email,
            PASSWORD: signupPassword?.toString() ?? "",
            SECRET_HASH: authSecretHash
          },
        });

        const authResponse = await clientRegion.send(authCommand);
        const accessToken = authResponse?.AuthenticationResult?.AccessToken;

        if (accessToken && authResponse?.$metadata?.httpStatusCode === 200) {
          // Save token securely in cookies (default to session cookie for signin confirmation)
          setAuthToken(accessToken, true);
        }

        toast.success("Account verified successfully!");
        // Keep signup data for profile setup step
        dispatch(setAuthView('profileSetup'));
      } else if (isSigninConfirmationFlow) {
        // Signin confirmation flow - Confirm Sign Up, then authenticate and save token
        const confirmCommand = new ConfirmSignUpCommand({
          ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
          Username: email,
          ConfirmationCode: otpString,
          SecretHash: secretHash,
        });

        await clientRegion.send(confirmCommand);

        // Now authenticate to get access token
        if (signinPassword) {
          const authSecretHash = generateSecretHash(email);
          const authCommand = new InitiateAuthCommand({
            AuthFlow: "USER_PASSWORD_AUTH",
            ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
            AuthParameters: {
              USERNAME: email,
              PASSWORD: signinPassword,
              SECRET_HASH: authSecretHash,
            },
          });

          const authResponse = await clientRegion.send(authCommand);
          const accessToken = authResponse?.AuthenticationResult?.AccessToken;

          if (accessToken && authResponse?.$metadata?.httpStatusCode === 200) {
            // Save token securely in cookies (default to session cookie for signin confirmation)
            setAuthToken(accessToken, false);
            toast.success("Account verified and logged in successfully!");
            dispatch(setSignupData(undefined));
            dispatch(setSigninPassword(undefined));
            dispatch(closeAuth());
            navigate("/");
          } else {
            throw new Error("Failed to authenticate after verification");
          }
        } else {
          throw new Error("Password not found. Please try logging in again.");
        }
      } else if (isForgotPasswordFlow) {
        // Cognito only validates the code via ConfirmForgotPassword (no separate verify API).
        // Use a one-time temp password, then New Password step uses InitiateAuth + ChangePassword.
        const tempPassword = generateForgotFlowTempPassword();
        const confirmForgotCommand = new ConfirmForgotPasswordCommand({
          ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
          Username: email,
          ConfirmationCode: otpString,
          Password: tempPassword,
          SecretHash: secretHash,
        });
        await clientRegion.send(confirmForgotCommand);
        dispatch(setForgotTempPassword(tempPassword));
        toast.success('Verification code accepted. Choose your new password.');
        dispatch(setAuthView('newPassword'));
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const name = err?.name;
      if (name === 'CodeMismatchException') {
        toast.error('Invalid verification code. Please try again.');
      } else if (name === 'ExpiredCodeException') {
        toast.error('This code has expired. Resend a new code.');
      } else if (name === 'LimitExceededException') {
        toast.error('Too many attempts. Please try again later.');
      } else {
        const errorMessage = err?.message || 'Verification failed';
        toast.error(errorMessage);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="figma-login-container">
      <div className="login-background-image">
        <div className="login-modal-container">
          <button
            type="button"
            className="auth-screen-back"
            onClick={handleBack}
            aria-label="Go back"
          >
            <img src={Images.back_arrow} alt="" aria-hidden />
          </button>
          <div className="login-header">
            <div className="logo-container">
              <img src={Images.main_logo} alt="CRICCENTER" className="logo" />
            </div>
            <h1 className="app-name">CRICCENTER</h1>
          </div>
          
          <h2 className="signin-title">Verify Email</h2>
          
          <form
            className="login-form"
            onSubmit={(e) => {
              e.preventDefault();
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              if (!otpComplete || isVerifying) {
                e.preventDefault();
                return;
              }
              e.preventDefault();
              void handleVerify();
            }}
          >
            <div className="form-group">
              <label className="form-label">
                <span className="label-text">
                  One Time Password has been sent to{' '}
                  <span className="email-text">{email || "your email"}</span>
                </span>
              </label>
              
              <div className="otp-inputs-container">
                {otpValues.map((value, index) => (
                  <input
                    key={index}
                    ref={(el) => { inputRefs.current[index] = el; }}
                    type="text"
                    className={`otp-input ${value ? 'filled' : ''}`}
                    value={value}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    maxLength={1}
                    autoComplete="off"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    disabled={isVerifying}
                  />
                ))}
              </div>
            </div>
            
            <div className="action-buttons">
              <button
                type="button"
                className="btn-primary"
                disabled={isVerifying || !otpComplete}
                onClick={() => void handleVerify()}
              >
                {isVerifying ? 'Verifying...' : 'Verify OTP'}
              </button>
            </div>
            
            <div className="resend-section">
              {isTimerActive ? (
                <p className="resend-timer">
                  <span className="timer-text">Resend Code in </span>
                  <span className="timer-count">{formatTime(timeLeft)}</span>
                </p>
              ) : (
                <button 
                  type="button" 
                  className="resend-button"
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OtpModal;
